import { Input } from "@/components/base/input/input";
import type { QuestionSettings } from "@/types/question";
import { parseNumericInput, sanitizeNumericInput } from "../../utils";

type NumberSettingsProps = {
	settings: QuestionSettings;
	setSettings: (settings: QuestionSettings) => void;
};

export const NumberSettings = ({
	settings,
	setSettings,
}: NumberSettingsProps) => (
	<div className="rounded-lg border border-gray-200 bg-white p-6">
		<h1 className="mb-4 font-semibold text-gray-900 text-lg">
			Configurações Numéricas
		</h1>
		<p className="mb-4 text-gray-600 text-sm">
			Deixe em branco para não impor limites.
		</p>

		<div className="grid grid-cols-2 gap-4">
			<div>
				<label
					className="mb-2 block font-medium text-gray-700 text-sm"
					htmlFor="min-value"
				>
					Valor mínimo
				</label>
				<Input
					id="min-value"
					inputMode="numeric"
					maxLength={9}
					onChange={(value) => {
						const sanitized = sanitizeNumericInput(value);
						// Verificar se o valor tem mais de 9 dígitos
						if (sanitized.length > 9) {
							return; // Não atualiza o estado se exceder 9 dígitos
						}
						const numValue = parseNumericInput(sanitized);
						if (numValue !== undefined) {
							if (numValue < 0) {
								setSettings({ ...settings, minValue: 0 });
								return;
							}
							if (numValue > 999_999_999) {
								return; // Não atualiza se exceder o limite
							}
						}
						setSettings({ ...settings, minValue: numValue });
					}}
					placeholder="Sem mínimo"
					type="text"
					value={settings.minValue?.toString() ?? ""}
				/>
				{settings.minValue !== undefined && settings.minValue < 0 && (
					<p className="mt-1 text-red-600 text-xs">
						O valor mínimo não pode ser negativo.
					</p>
				)}
			</div>

			<div>
				<label
					className="mb-2 block font-medium text-gray-700 text-sm"
					htmlFor="max-value"
				>
					Valor máximo
				</label>
				<Input
					id="max-value"
					inputMode="numeric"
					maxLength={9}
					onChange={(value) => {
						const sanitized = sanitizeNumericInput(value);
						// Verificar se o valor tem mais de 9 dígitos
						if (sanitized.length > 9) {
							return; // Não atualiza o estado se exceder 9 dígitos
						}
						const numValue = parseNumericInput(sanitized);
						if (numValue !== undefined) {
							if (numValue < 0) {
								setSettings({ ...settings, maxValue: 0 });
								return;
							}
							if (numValue > 999_999_999) {
								return; // Não atualiza se exceder o limite
							}
							if (numValue <= 0) {
								return;
							}
						}
						setSettings({ ...settings, maxValue: numValue });
					}}
					placeholder="Sem máximo"
					type="text"
					value={settings.maxValue?.toString() ?? ""}
				/>
				{settings.maxValue !== undefined && settings.maxValue <= 0 && (
					<p className="mt-1 text-red-600 text-xs">
						O valor máximo deve ser maior que zero.
					</p>
				)}
			</div>
		</div>
	</div>
);
